import { IElement } from "core-integration/src/store/elements/types";
import { useEffect, useRef } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import styled from "styled-components";
import i18n from "../../../i18n";

declare global {
  interface Window {
    PM?: any;
  }
}

const StyledWidget = styled.div<StyledWidgetProps>`
  .visx-group {
    & > *:nth-last-child(2) {
      &[stroke="#256fea"] {
        stroke: #256fea;
      }
      &[fill="#256fea"] {
        fill: #256fea;
      }

      stroke: #256fea;
      fill: #256fea;
    }
  }
  .pm-player-legend-average-square {
    background-color: #256fea;
  }
  .pm-logo-text,
  .pm-data-powered-text,
  .pm-fallback-container {
    color: ${({ theme }) => theme.colors.white};
  }
  .pm-fallback-container {
    padding: ${({ theme }) => theme.space[4]} 0;
    font-family: ${({ theme }) => theme.fonts.base};
    font-size: ${({ theme }) => theme.fontSizes[2]};
    font-weight: bold;
  }
  .pm-player-bio {
    background-color: ${({ theme }) => theme.colors.white};
    display: ${({ $showWidgetSummary }) =>
      $showWidgetSummary ? "block" : "none"};
  }
  .pm-player-bio-name,
  .pm-player-bio-details,
  .pm-player-bio-posgroup {
    color: ${({ theme }) => theme.colors.black};
  }
  .pm-player-radar-container {
    width: 100%;
    margin-top: ${({ theme }) => `-${theme.space[3]}`};
    padding: ${({ theme }) => theme.space[2]} 0;
  }
  .pm-flex-row {
    width: auto;
  }
`;
interface StyledWidgetProps {
  $showWidgetSummary: boolean;
}

interface WidgetProps extends WithTranslation {
  selectorId: string;
  element: IElement | null;
  onWidgetChange?: (visible: boolean) => void;
  showWidgetSummary?: boolean;
}
const positionKPIS: { [key: number]: string[] } = {
  1: [
    "CleanSheets",
    "Saves",
    "DefensiveActions90",
    "GoalsConceded90",
    "SavesPct",
    "ShotsAgainst90",
    "KeyPasses90",
  ],
  2: [
    "CleanSheets",
    "DefensiveActions90",
    "Goals",
    "Assists",
    "ShotAssists",
    "xG",
    "xA",
    "KeyPasses90",
  ],
  3: [
    "Goals",
    "Assists",
    "DefensiveActions90",
    "ShotAssists",
    "KeyPasses90",
    "xG",
    "xA",
  ],
  4: ["Goals", "Assists", "ShotAssists", "KeyPasses90", "xG", "xA"],
};

const PlayerWidget: React.FC<WidgetProps> = ({
  selectorId,
  element,
  onWidgetChange,
  showWidgetSummary = true,
}: WidgetProps) => {
  const playerWidgetRef = useRef<any>(null);
  const PMWidget = (window as any).PM;

  // Unmount-only cleanup
  useEffect(() => {
    return () => {
      if (playerWidgetRef.current) {
        playerWidgetRef.current.remove();
        playerWidgetRef.current = null;
        onWidgetChange?.(false);
      }
    };
  }, []);

  // Covers widget lifecycle — no cleanup, so ref survives between runs
  // if element changes, but component doesn't unmount
  useEffect(() => {
    if (!element || !PMWidget) return;

    if (playerWidgetRef.current) {
      playerWidgetRef.current.update({ fantasyId: element.code });
    } else {
      playerWidgetRef.current = PMWidget("addPlayerWidget", {
        selectorId,
        fantasyId: element.code,
        rules: {
          theme: "dark",
          locale: i18n.language,
          kpis: positionKPIS[element.element_type],
        },
      });
      onWidgetChange?.(true);
    }
  }, [element, onWidgetChange, PMWidget]);

  return (
    <StyledWidget id={selectorId} $showWidgetSummary={showWidgetSummary} />
  );
};

export default withTranslation()(PlayerWidget);
